/**
 * DirectoryHub — list + search page (Block 6.F.2 §3.5; TypeScript on
 * PageShell per UX-13 S1).
 *
 * UX-13 S1: the page sits on PageShell's `bleed` mode — the mode the
 * rule-of-three trigger minted when this page became the third banded
 * surface (after Hub and the AiTools landing). The shell contributes the
 * <main data-testid> element; the sage-400 band and the max-w-6xl inner
 * containers below are the page's own, byte-preserved from the .jsx.
 * Gate parity: the directory had no access gate before the shell and
 * gains none with it (canSuggest gates only the CTA).
 *
 * Replaces src/pages/Directory.tsx (deleted at the original port).
 * Renders peace-hub-style search + filter UX wired to peace-os data via
 * useUnifiedSearch.
 *
 * URL state machine (§2.4):
 *   ?q=<freetext>
 *   ?view=<all|organizations|people>     (default 'all' if absent)
 *   ?categories=<comma-separated slugs>
 *   ?subcategories, ?specialties, ?counties, ?highlights, ?service_models,
 *   ?payment_types, ?contact_specialties=<comma-separated display-names>
 *                                        (each value's own commas escaped;
 *                                        src/lib/directorySearchParams.ts)
 *   ?has_notes=1, ?is_referral_partner=1, ?sort=<mode>
 *   ?show=<n>                            (paging depth; absent at the first page)
 *   ?recommended=1                       (only recommended organizations)
 *   ?map=1                               (the map view instead of the list)
 *   ?near=<clientId>                     (the map centred on one client's current location)
 *
 * List and map are two views of ONE search: the search box and the
 * filters carry across the toggle, and the map draws
 * exactly the organizations and the people the lists would show
 * (DirectoryMapView takes orgResults and contactResults, each empty in
 * the view that hides it). ?map and ?near are read straight off the
 * URL, never copied into state, so a client's record can link to the
 * map already centred on that client and the browser's back button
 * undoes it.
 *
 * The layout: the band carries All / Organizations / People, List | Map,
 * Suggest and the search. On the list, under it: the filter row, the
 * category tiles while browsing, one line with "Showing N of M" and
 * Sort, then the results. On Map the tiles and that line go and the
 * filter row moves into the map's sticky bar (DirectoryMapView's
 * filterRow). The
 * layers a person switched by hand are kept here, so a visit to List and
 * back keeps them; leaving the Directory drops them.
 *
 * Back to the same results: everything above is URL state, so the
 * browser's back button restores it. Cards open through the router (not
 * a full document load), the page remembers its URL for the detail
 * pages' "Directory" control, and it saves its scroll position on the
 * way out and applies it once the results are back on screen (see
 * src/lib/directoryReturn.ts).
 *
 * Feature-parity matrix per §3.5: search input + URL state preserved,
 * type-chip → view-toggle, STATE chip dropped (Phase 1 single-state),
 * Bulk-add steward button REMOVED per Block 6.G.2 B4 (admin function;
 * lives on /admin-review?tab=bulk), ErrorBanner + SkeletonRow + aria-
 * live status preserved, "Show more" pagination (visibleCount += 30).
 *
 * §m: useUnifiedSearch maintains its own internal query/filter state
 * AND DirectoryHub mirrors via URL params for shareable links. Sync is
 * one-way at mount: URL params seed initial state on first render;
 * subsequent state changes update URL via useSearchParams.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { List as ListIcon, MapPin, Plus } from 'lucide-react'
import { PageShell, PAGE_WIDTH_CLASSES, PAGE_GUTTER } from '@/components/primitives/PageShell'
import { BrandWatermark } from '@/components/primitives/BrandWatermark'
import { Button } from '@/components/primitives/Button'
import { SegmentedToggle } from '@/components/primitives/SegmentedToggle'
import { useTenant } from '@/lib/auth'
import { canReadClinicalRecord, canSuggest } from '@/lib/capabilities'
import { useUnifiedSearch } from '@/hooks/useUnifiedSearch'
import { SearchBar } from '@/components/peace-hub-port/SearchBar'
import { CategoryTiles } from '@/components/peace-hub-port/CategoryTiles'
import { FilterBar, ContactFilterBar, type DirectoryFilters } from '@/components/peace-hub-port/FilterBar'
import { SortControl } from '@/components/peace-hub-port/SortControl'
import { ResultsList } from '@/components/peace-hub-port/ResultsList'
import { DirectoryMapView, type DirectoryMapViewMode } from '@/components/peace-hub-port/DirectoryMapView'
import { ContactCard } from '@/components/peace-hub-port/ContactCard'
import { ErrorBanner } from '@/components/ErrorBanner'
import { SkeletonRow } from '@/components/Skeleton'
import { SuggestResourceForm } from '@/components/peace-hub-port/SuggestResourceForm'
import {
  rememberDirectoryScroll,
  rememberDirectorySearch,
  takeDirectoryScroll,
} from '@/lib/directoryReturn'
import { searchStateFromParams, writeSearchState } from '@/lib/directorySearchParams'
import type { IndividualForSearch, ProviderForSearch } from '@/lib/types'
import type { DirectoryMapLayerOverrides } from '@/lib/directoryMapLayers'
import { signedInName } from '@/lib/signedInName'

const VIEW_TABS = [
  { value: 'all', label: 'All', suffix: 'all' },
  { value: 'organizations', label: 'Organizations', suffix: 'organizations' },
  { value: 'people', label: 'People', suffix: 'people' },
] as const

const DISPLAY_TABS = [
  { value: 'list', label: 'List', Icon: ListIcon },
  { value: 'map', label: 'Map', Icon: MapPin },
] as const

/** List or map: two ways of looking at the same filtered search, so text
 *  tabs rather than a third pill switch. The chosen tab is cream over a
 *  gold underline (the eyebrow's gold), the other muted cream (5.98:1 on
 *  the band). The focus ring is cream: the house sage ring is 2.28:1 here. */
function ListMapSwitch({ mapView, onChange }: { mapView: boolean; onChange: (map: boolean) => void }): ReactNode {
  return (
    <div role="group" aria-label="List or map" data-testid="directory-view" className="flex items-center gap-4">
      {DISPLAY_TABS.map(({ value, label, Icon }) => {
        const pressed = (value === 'map') === mapView
        return (
          <Button
            key={value}
            variant="ghost-on-dark"
            aria-pressed={pressed}
            onClick={() => onChange(value === 'map')}
            data-testid={`directory-view-${value}`}
            className={
              '!gap-1.5 !rounded-none !px-0.5 border-b-2 hover:!bg-transparent focus-visible:!ring-cream/70 ' +
              (pressed ? '!text-cream border-warm-400' : '!text-cream/70 border-transparent hover:!text-cream')
            }
          >
            <Icon size={14} aria-hidden="true" />
            <span>{label}</span>
          </Button>
        )
      })}
    </div>
  )
}

export function DirectoryHub(): ReactNode {
  const { tenantSlug, whoami, principal } = useTenant()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  // X.5.1 — the "+ Suggest Resource" CTA moved here from the AppShell
  // header (header stripped to brand + bell + avatar). Gated on canSuggest.
  const [suggestOpen, setSuggestOpen] = useState(false)
  const canSubmit = canSuggest(whoami)

  // The URL is the source of truth at mount: the search state starts from
  // it, so the first render already shows the search the URL names (and
  // the sync effect below has nothing to overwrite).
  const [initialSearch] = useState(() => searchStateFromParams(searchParams))

  const {
    query,
    setQuery,
    viewMode,
    setViewMode,
    orgResults,
    contactResults,
    contacts,
    visibleCount,
    showMore,
    sortMode,
    setSortMode,
    filters: filtersUntyped,
    toggleFilter,
    toggleBoolFilter,
    clearFilter,
    clearAllFilters,
    setCategoryFilter,
    hasActiveFilters,
    resources,
    loading,
    error,
    loaded,
    refetch,
  } = useUnifiedSearch(initialSearch)

  // useUnifiedSearch is still plain JS (its filters state initializes as {},
  // which TS infers as the empty object type); type the boundary once here.
  const filters: DirectoryFilters = filtersUntyped

  function changeView(nextView: string) {
    if (nextView === viewMode) return
    clearAllFilters()
    setViewMode(nextView)
  }

  // Sync state changes back to URL. Intentionally omits setSearchParams
  // from deps (stable identity per react-router-dom v7). The reader at
  // mount and this writer are one pair in src/lib/directorySearchParams.ts.
  useEffect(() => {
    setSearchParams(
      (prev) => writeSearchState(prev, { query, viewMode, filters, sortMode, visibleCount }),
      { replace: true },
    )
  }, [query, viewMode, filters, sortMode, visibleCount])

  // The way back: remember this tab's current directory URL for the
  // detail pages' "Directory" control, and keep the current search in a
  // ref so the unmount cleanup can save the scroll position against it.
  const searchRef = useRef(location.search)
  useEffect(() => {
    searchRef.current = location.search
    rememberDirectorySearch(`${location.pathname}${location.search}`)
  }, [location.pathname, location.search])
  useEffect(() => {
    return () => {
      rememberDirectoryScroll(searchRef.current, window.scrollY)
    }
  }, [])
  // Apply a remembered scroll position once the results are on screen —
  // the browser's own restoration fires before this page has rendered
  // anything to scroll to. Taken on read, so it applies to one arrival.
  useEffect(() => {
    if (loading) return
    const y = takeDirectoryScroll(location.search)
    if (y !== null) window.scrollTo(0, y)
  }, [loading, location.search])

  // The map view and its picked client live on the URL only. A ?near
  // without ?map still means the map: the link from a client's record
  // names both, and a hand-edited URL naming only the client should not
  // land on a list that cannot show them.
  const nearClientId = searchParams.get('near')
  const mapView = searchParams.get('map') === '1' || nearClientId !== null
  function setMapView(on: boolean) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev)
      if (on) sp.set('map', '1')
      else {
        sp.delete('map')
        sp.delete('near')
      }
      return sp
    }, { replace: true })
  }
  // The map layers switched by hand, kept across List and back.
  const [mapLayerOverrides, setMapLayerOverrides] = useState<DirectoryMapLayerOverrides>({})
  function setNearClient(clientId: string | null) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev)
      // Picking or clearing a client is a map action: the map stays.
      sp.set('map', '1')
      if (clientId) sp.set('near', clientId)
      else sp.delete('near')
      return sp
    }, { replace: true })
  }

  const showOrgs = viewMode !== 'people'
  const showContacts = viewMode !== 'organizations'
  const mapViewMode: DirectoryMapViewMode = viewMode === 'people' ? 'people' : viewMode === 'organizations' ? 'organizations' : 'all'
  // Counts and "No resources found" only once both lists loaded: while
  // loading, paused or after a failure they would read as zero.
  const listsLoaded = loaded
  // Browsing: the organizations list with no search the search acts on
  // and no filter on. Only then do the category tiles show.
  const browsing = !mapView && viewMode !== 'people' && query.trim().length < 2 && !hasActiveFilters
  const resultCount = viewMode === 'people'
    ? contactResults.length
    : viewMode === 'organizations'
      ? orgResults.length
      : orgResults.length + (query.trim().length >= 2 ? contactResults.length : 0)

  // In-app navigation: the directory stays in this document's history
  // with its URL state, so the browser's back button returns to it.
  function handleSelectResource(resource: ProviderForSearch) {
    navigate(`/t/${encodeURIComponent(tenantSlug)}/organizations/${encodeURIComponent(resource.id)}`)
  }
  function handleSelectContact(contact: IndividualForSearch) {
    navigate(`/t/${encodeURIComponent(tenantSlug)}/individuals/${encodeURIComponent(contact.id)}`)
  }

  // The filter row for the view: the organizations' facets, or the people
  // view's own. The list shows it directly under the band; the map, in its
  // sticky bar, so the filters stay in reach while the map scrolls.
  function filterRow(): ReactNode {
    return viewMode !== 'people' ? (
      <FilterBar
        filters={filters}
        resources={resources}
        onToggleFilter={toggleFilter}
        onToggleBool={toggleBoolFilter}
        onClearFilter={clearFilter}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
      />
    ) : (
      <ContactFilterBar
        contacts={contacts}
        filters={filters}
        onToggleFilter={toggleFilter}
        onToggleBool={toggleBoolFilter}
        onClearFilter={clearFilter}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
      />
    )
  }

  return (
    <PageShell maxWidth="bleed" testId="page-directory">
      {/* DL v1 (2026-07-06): the wide DISPLAY band is the FOREST band,
          family-wide (bands are forest, always). Post-#445 the band carries
          the search + display title only (the category grid moved to the
          body); on-band text is already lightened, which reads even cleaner
          on the darker forest. */}
      <div data-testid="directory-band" className="relative overflow-hidden bg-sage-800 pb-7">
      {/* The tenant's mark as a band watermark (Amanda 2026-07-03 sizing
          ruling: the mark spans the banner's height, aspect preserved,
          slid half off the page). Content sits in the relative wrapper
          below so it paints above the mark. Cream at 7% on the mid-tone
          sage band reads as quiet as 5% does on forest. */}
      <BrandWatermark fit="height" className="inset-y-0 right-0 translate-x-[45%] text-cream/[7%]" />
      <div className="relative">
      {/* Alignment hotfix (founder walk 2026-07-06): the band regained its
          DL1 identity — eyebrow + serif display title FIRST (every other
          page has one), then the tab row + search. Type steps mirror
          SectionBand's; the container is the page's own wide+gutter
          column so the title's left edge == the body's left edge. */}
      <div
        data-testid="directory-band-frame"
        className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-5 md:pt-6`}
      >
        <div className="mb-3 inline-flex items-center gap-2 text-[0.71875rem] font-semibold uppercase tracking-[0.16em] text-warm-400">
          <span aria-hidden="true" className="h-px w-[22px] bg-warm-400" />
          Olive · Resources
        </div>
        <h1 className="font-display text-[1.875rem] md:text-[2.625rem] font-semibold tracking-[-0.01em] text-cream">
          Directory
        </h1>
      </div>
      {/* View-mode toggle + (X.5.1) Suggest Resource CTA. */}
      <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} py-4`}>
        {/* One wrapping line: the view switch at the left, List | Map and
            Suggest at the right end. Below sm the view switch is the full
            width of the band and the other two share the line under it. */}
        <div className="flex items-center gap-x-5 gap-y-3 flex-wrap">
          <SegmentedToggle
            options={VIEW_TABS}
            value={viewMode}
            onChange={changeView}
            name="Directory view"
            testIdPrefix="directory-band-view-toggle"
          />
          {/* In every view: the people view places people who have no
              organization. */}
          <div data-testid="directory-view-toggle" className="sm:ml-auto">
            <ListMapSwitch mapView={mapView} onChange={setMapView} />
          </div>
          {/* Secondary-on-dark (DL v1): a white-bordered ghost sitting on the
              band, not a third tan style. Suggesting is the band's secondary
              action; the R1 family gives it the ghost-on-dark treatment. */}
          {canSubmit && (
            <Button
              type="button"
              onClick={() => setSuggestOpen(true)}
              data-testid="suggest-resource-button"
              variant="secondary-on-dark"
              aria-label="Suggest a new resource"
            >
              <span aria-hidden="true" className="grid h-5 w-5 shrink-0 place-items-center rounded-tight bg-cream/15 text-cream">
                <Plus size={14} />
              </span>
              <span>Suggest Resource</span>
            </Button>
          )}
        </div>
      </div>

      <SearchBar
        query={query}
        onQueryChange={setQuery}
        resultCount={listsLoaded ? resultCount : null}
        hasFilters={hasActiveFilters}
        viewMode={viewMode}
        showCount={false}
      />
      </div>
      </div>
      {/* ── end forest band ── */}

      {/* The list's order: the band, then the filter row, then (browsing
          only) the category tiles, then the Showing and Sort line, then the
          results. The map takes the filter row into its bar. */}
      {!mapView && filterRow()}

      {/* The tiles are for browsing: shown while there is no search (under
          2 characters, the length the search acts on) and no filter on, and
          folded away as soon as either is set. Selecting a tile sets the
          category filter, synced with the FilterBar dropdown (X.6.5B). */}
      {browsing && (
        <CategoryTiles
          resources={resources}
          activeCategorySlugs={filters.categories ?? []}
          onSelect={setCategoryFilter}
          showCounts={listsLoaded}
        />
      )}

      {/* The count line, the list's only: "Showing N of M" for the
          organizations once both lists are in (while either is loading or
          failed a count would mislead), then Sort at the right (it orders
          the people too; the map has no order). */}
      {!mapView && (
        <div
          data-testid="directory-count-line"
          className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-3 flex flex-wrap items-center gap-x-3 gap-y-2`}
        >
          <p className="text-[13px] font-medium text-ink-soft" aria-live="polite" data-testid="directory-showing">
            {showOrgs && listsLoaded && orgResults.length > 0
              ? `Showing ${Math.min(visibleCount, orgResults.length).toLocaleString()} of ${orgResults.length.toLocaleString()}`
              : '\u00a0'}
          </p>
          {/* min-w-0 and max-w-full: the cluster shrinks to the line at
              phone zoom, and the pill's own max-w-full then holds it. */}
          <div className="ml-auto flex min-w-0 max-w-full items-center gap-2">
            <span className="text-xs font-medium text-sage-300">Sort</span>
            <SortControl value={sortMode} onChange={setSortMode} />
          </div>
        </div>
      )}

      {/* Block 6.G.2 B4: Steward-only Bulk Add affordance removed from
          the consumer directory header. Bulk-add is admin work; it lives
          on the admin-review surface (/t/:tenant/admin-review?tab=bulk).
          Removing it from /directory keeps the consumer surface clean
          per Amanda's framing. */}

      {error && (
        <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-3`}>
          <ErrorBanner error={error} onRetry={refetch} what="the directory" />
        </div>
      )}

      {/* The map lays out its own full-width bar and column. */}
      {mapView && (
        <DirectoryMapView
          tenantSlug={tenantSlug}
          organizations={showOrgs ? orgResults : []}
          individuals={showContacts ? contactResults : []}
          view={mapViewMode}
          canShowClients={canReadClinicalRecord(whoami)}
          nearClientId={nearClientId}
          onNearClientChange={setNearClient}
          filtersActive={hasActiveFilters}
          searchActive={query.trim() !== ''}
          initialLayerOverrides={mapLayerOverrides}
          onLayerOverridesChange={setMapLayerOverrides}
          filterRow={filterRow()}
        />
      )}

      {/* The results container was a <main> in the .jsx; PageShell's bleed
          mode now provides the page's single <main>, so this is a <div>. */}
      {!mapView && (
      <div data-testid="directory-body" className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} py-4 pb-24`}>

        {!mapView && loading && orgResults.length === 0 && contactResults.length === 0 && (
          <div className="space-y-2" role="list">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        )}

        {!mapView && showContacts && contactResults.length > 0 && query.trim().length >= 2 && (
          <div className="mb-6">
            <p className="text-[0.8125rem] text-ink-soft font-medium mb-3" aria-live="polite">
              {contactResults.length} contact{contactResults.length !== 1 ? 's' : ''} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {contactResults.slice(0, viewMode === 'all' ? 6 : undefined).map((contact: IndividualForSearch) => (
                <ContactCard key={contact.id} contact={contact} onClick={handleSelectContact} />
              ))}
            </div>
            {viewMode === 'all' && contactResults.length > 6 && (
              <Button
                onClick={() => changeView('people')}
                variant="ghost"
                className="mt-2"
              >
                + {contactResults.length - 6} more contacts
              </Button>
            )}
          </div>
        )}

        {!mapView && viewMode === 'people' && query.trim().length < 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {contactResults.map((contact: IndividualForSearch) => (
              <ContactCard key={contact.id} contact={contact} onClick={handleSelectContact} />
            ))}
          </div>
        )}

        {!mapView && showOrgs && (listsLoaded || orgResults.length > 0) && (
          <ResultsList
            showCount={false}
            results={orgResults}
            visibleCount={visibleCount}
            onShowMore={showMore}
            onSelect={handleSelectResource}
          />
        )}
      </div>
      )}

      {/* X.5.1 — SuggestResourceForm BottomSheet, relocated from AppShell.
          Opens from the "+ Suggest Resource" CTA above. */}
      {canSubmit && (
        <SuggestResourceForm
          isOpen={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          tenantSlug={tenantSlug}
          submitterDisplayName={signedInName(whoami, principal) || null}
        />
      )}
    </PageShell>
  )
}
